import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './ui/Button';

// Two items, and each one downloads on the click. There is no picker in front of
// Excel: the owner replaced every uploaded layout with ONE fixed United Ship
// Chandlers Excel, built on the server (2026-09-29), so there is nothing left to
// choose between.
interface ExportDropdownProps {
  onExportPDF: () => void;
  onExportExcel: () => void;
  disabled?: boolean;
  /** The button's tooltip, e.g. why it is disabled. */
  title?: string;
}

const ExportDropdown: React.FC<ExportDropdownProps> = ({
  onExportPDF,
  onExportExcel,
  disabled = false,
  title
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      if (!isOpen) setDropdownPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownMenuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportPDF = () => {
    setIsOpen(false);
    onExportPDF();
  };

  const handleExportExcel = () => {
    setIsOpen(false);
    onExportExcel();
  };

  const dropdownMenu = isOpen && dropdownPosition && (
    <div
      ref={dropdownMenuRef}
      className="fixed bg-white border border-slate-200 rounded-md shadow-lg z-[9999] min-w-[200px] py-1"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
    >
      <button
        onClick={handleExportPDF}
        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.05 1.03613L14.832 3.93613V14.9641H4.43945V15.0001H14.8675V3.97263L12.05 1.03613Z" fill="#909090"/>
          <path d="M12.0153 1H4.40381V14.964H14.8318V3.9365L12.0153 1Z" fill="#F4F4F4"/>
          <path d="M4.32732 1.75H1.13232V5.1635H11.1823V1.75H4.32732Z" fill="#7A7B7C"/>
          <path d="M11.2358 5.10545H1.19727V1.68945H11.2358V5.10545Z" fill="#DD2025"/>
        </svg>
        PDF
      </button>
      <button
        onClick={handleExportExcel}
        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.79036 7.6752L4.25586 6.7002V13.9047C4.25579 13.983 4.27118 14.0605 4.30115 14.1329C4.33112 14.2052 4.37507 14.271 4.43048 14.3263C4.4859 14.3816 4.55169 14.4254 4.62409 14.4553C4.69648 14.4851 4.77405 14.5004 4.85236 14.5002H14.4024C14.4807 14.5005 14.5584 14.4854 14.6309 14.4556C14.7034 14.4258 14.7694 14.382 14.8249 14.3266C14.8804 14.2713 14.9244 14.2055 14.9545 14.1331C14.9845 14.0607 14.9999 13.9831 14.9999 13.9047V11.2502L9.79036 7.6752Z" fill="#185C37"/>
          <path d="M9.79036 1.5H4.85236C4.77405 1.4998 4.69648 1.51507 4.62409 1.54491C4.55169 1.57475 4.4859 1.6186 4.43048 1.67392C4.37507 1.72924 4.33112 1.79496 4.30115 1.8673C4.27118 1.93965 4.25579 2.0172 4.25586 2.0955V4.75L9.79036 8L12.7209 8.975L14.9999 8V4.75L9.79036 1.5Z" fill="#21A366"/>
          <path d="M4.25586 4.75H9.79036V8H4.25586V4.75Z" fill="#107C41"/>
        </svg>
        Excel
      </button>
    </div>
  );

  return (
    <>
      <div className="relative" ref={triggerRef}>
        <Button onClick={() => setIsOpen(!isOpen)} disabled={disabled} title={title} aria-haspopup="menu" aria-expanded={isOpen}>
          Export
        </Button>
      </div>

      {dropdownMenu && createPortal(dropdownMenu, document.body)}
    </>
  );
};

export default ExportDropdown;
